import { useState } from 'react'
import { EventHistoryList } from 'components/NewCompanyPage/EventHistory/EventHistoryList'
import { TeamMemberFilter } from 'components/NewCompanyPage/EventHistory/filters/TeamMemberFilter'
import { useEventHistoryQuery } from 'components/NewCompanyPage/EventHistory/useEventHistoryQuery'
import { buildEmailFromEvent } from 'components/NewCompanyPage/EventHistory/utils'
import { EmailsModal } from 'components/NewCompanyPage/Modals/EmailsModal/EmailsModal'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'
import type { EventHistoryTab } from 'components/NewCompanyPage/EventHistory/EventHistoryAccordion'
import type { EmailMessage } from 'models/emailIntegration'
import { DateRangePicker, DateRangeValue } from './DateRangePicker'

interface DealEventHistoryProps {
  dealId: number
  stickyTopOffset?: number
  /** Restrict the timeline to a specific event type (e.g. 'emails'
   *  for the Emails tab). Defaults to 'all'. */
  selectedTab?: EventHistoryTab
}

/**
 * Deal-scoped activity feed. Reuses the same sub-components as the company
 * page EventHistoryAccordion but without the accordion wrapper, company-specific
 * tabs (Deals, Samples, Account Attributes), or sample preview modal.
 */
export function DealEventHistory({
  dealId,
  stickyTopOffset = 0,
  selectedTab = 'all',
}: DealEventHistoryProps) {
  const [dateRange, setDateRange] = useState<DateRangeValue>(undefined)

  // Shared-filter-store reset moved to DealDetailPage so it only fires
  // when the user navigates to a different deal, not every time the
  // user switches between Activity / Emails tabs (which unmount and
  // remount this component).

  const {
    groupedEvents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useEventHistoryQuery({
    dealId,
    selectedTab,
    dateRange,
  })

  // Email view modal state
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null)

  return (
    <StageColorProvider value="pipeline">
      <div>
        {/* Filters — sticky below main header */}
        <div
          className="sticky z-[19] bg-surface pb-2 flex flex-col gap-2"
          style={{ top: stickyTopOffset }}
        >
          <div className="flex items-center gap-2">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <TeamMemberFilter inline />
          </div>
        </div>

        {/* Event List */}
        <EventHistoryList
          isLoading={isLoading}
          error={error}
          groupedEvents={groupedEvents}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          onPreviewRequest={() => {}}
          onEmailClick={(event) => setSelectedEmail(buildEmailFromEvent(event))}
        />

        {/* Email View Modal */}
        {selectedEmail && (
          <EmailsModal
            show={!!selectedEmail}
            onHide={() => setSelectedEmail(null)}
            emailOnlyMode
            initialEmail={selectedEmail}
          />
        )}
      </div>
    </StageColorProvider>
  )
}
